import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FileDto, DocumentType, DOCUMENT_TYPE_LABELS, getDocumentTypeOptions } from '../../../../../types/file.types';
import { getFileUrl } from '../../../../../utils/file.utils';
import { useTranslation } from 'react-i18next';
import { Select, Empty } from 'antd';
import { User } from '../../../../../types/user.types';

interface EmployeesDocumentsProps {
  documents: FileDto[];
  userData: User;
}

const EmployeesDocuments: React.FC<EmployeesDocumentsProps> = ({ documents, userData }) => {
  const { t } = useTranslation();
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'available' | 'missing' | DocumentType>('all');
  const [selectedCategory, setSelectedCategory] = useState<DocumentType | 'all'>('all');

  const getFileIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'pdf':
        return 'ti ti-file-type-pdf text-danger';
      case 'doc':
      case 'docx':
        return 'ti ti-file-type-doc text-primary';
      case 'xls':
      case 'xlsx':
        return 'ti ti-file-type-xls text-success';
      case 'jpg':
      case 'jpeg':
      case 'png':
        return 'ti ti-file-type-jpg text-warning';
      default:
        return 'ti ti-file text-secondary';
    }
  };

  const getFileType = (path: string) => {
    return path.split('.').pop()?.toLowerCase() || 'unknown';
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  // Belgeleri kategorilere göre grupla
  const categorizedDocuments = useMemo(() => {
    const categories: Record<DocumentType, FileDto[]> = {} as Record<DocumentType, FileDto[]>;
    const uncategorized: FileDto[] = [];

    // Tüm kategorileri başlat (askerlik durum belgesi sadece erkeklerde)
    Object.values(DocumentType).forEach(type => {
      if (type === DocumentType.MILITARY_SERVICE_CERTIFICATE && userData?.gender !== 'male') {
        return; // Erkek değilse askerlik durum belgesini ekleme
      }
      categories[type] = [];
    });

    // Belgeleri kategorilere ayır
    documents.forEach(doc => {
      if (doc.documentType && Object.values(DocumentType).includes(doc.documentType)) {
        categories[doc.documentType].push(doc);
      } else {
        uncategorized.push(doc);
      }
    });

    return { categories, uncategorized };
  }, [documents, userData?.gender]);

  // Eksik belge tiplerini bul
  const missingDocumentTypes = useMemo(() => {
    return Object.values(DocumentType).filter(type => {
      // Askerlik durum belgesini sadece erkeklerde göster
      if (type === DocumentType.MILITARY_SERVICE_CERTIFICATE) {
        return userData?.gender === 'male' && categorizedDocuments.categories[type].length === 0;
      }
      return categorizedDocuments.categories[type].length === 0;
    });
  }, [categorizedDocuments, userData?.gender]);

  // Filtrelenmiş belgeleri al
  const filteredData = useMemo(() => {
    if (selectedFilter === 'all') {
      return {
        availableCategories: categorizedDocuments.categories,
        missingTypes: missingDocumentTypes,
        uncategorized: categorizedDocuments.uncategorized
      };
    } else if (selectedFilter === 'available') {
      const availableCategories: Record<DocumentType, FileDto[]> = {} as Record<DocumentType, FileDto[]>;
      Object.entries(categorizedDocuments.categories).forEach(([type, docs]) => {
        if (docs.length > 0) {
          availableCategories[type as DocumentType] = docs;
        }
      });
      return {
        availableCategories,
        missingTypes: [],
        uncategorized: categorizedDocuments.uncategorized
      };
    } else if (selectedFilter === 'missing') {
      return {
        availableCategories: {} as Record<DocumentType, FileDto[]>,
        missingTypes: missingDocumentTypes,
        uncategorized: []
      };
    } else {
      // Belirli bir kategori seçildi
      const availableCategories: Record<DocumentType, FileDto[]> = {} as Record<DocumentType, FileDto[]>;
      if (categorizedDocuments.categories[selectedFilter as DocumentType]) {
        availableCategories[selectedFilter as DocumentType] = categorizedDocuments.categories[selectedFilter as DocumentType];
      }
      return {
        availableCategories,
        missingTypes: categorizedDocuments.categories[selectedFilter as DocumentType].length === 0 ? [selectedFilter as DocumentType] : [],
        uncategorized: []
      };
    }
  }, [selectedFilter, categorizedDocuments, missingDocumentTypes]);

  // Kategori badge rengini belirle
  const getCategoryBadgeColor = (type: DocumentType) => {
    const colors: Record<DocumentType, string> = {
      [DocumentType.ID_CARD]: 'processing',
      [DocumentType.ID_PHOTO]: 'success',
      [DocumentType.PASSPORT]: 'purple',
      [DocumentType.DRIVER_LICENSE]: 'cyan',
      [DocumentType.DIPLOMA]: 'gold',
      [DocumentType.CERTIFICATE]: 'lime',
      [DocumentType.HEALTH_REPORT]: 'red',
      [DocumentType.CRIMINAL_RECORD]: 'orange',
      [DocumentType.RESIDENCE]: 'blue',
      [DocumentType.SGK_ENTRY_DOCUMENT]: 'green',
      [DocumentType.EMPLOYMENT_CONTRACT]: 'yellow',
      [DocumentType.MILITARY_SERVICE_CERTIFICATE]: 'pink'
    };
    return colors[type] || 'default';
  };

  return (
    <div className="accordion-item">
      <div className="accordion-header" id="headingTwo">
        <div 
          className="accordion-button collapsed"
          data-bs-toggle="collapse"
          data-bs-target="#primaryBorderOne"
          aria-expanded="false"
          aria-controls="primaryBorderOne"
          style={{ cursor: 'pointer' }}
        >
          <div className="d-flex align-items-center justify-content-between flex-fill">
            <div className="d-flex align-items-center">
              <h5 className="mb-0 me-3">{t('employeesDetail.documents.title')}</h5>
            </div>
            <div className="d-flex">
              <i className="ti ti-chevron-down fs-18" />
            </div>
          </div>
        </div>
      </div>
      <div
        id="primaryBorderOne"
        className="accordion-collapse collapse border-top"
        aria-labelledby="headingTwo"
        data-bs-parent="#accordionExample"
      >
        <div className="accordion-body">
          {/* Filtre Kontrolleri */}
                    <div className="row mb-4">
            <div className="col-md-6">
              <label className="form-label small text-muted">Belge Durumu</label>
              <Select
                style={{ width: '100%' }}
                value={selectedFilter}
                onChange={setSelectedFilter}
                options={[
                  { value: 'all', label: 'Tümü' },
                  { value: 'available', label: 'Mevcut Belgeler' },
                  { value: 'missing', label: 'Eksik Belgeler' },
                  ...getDocumentTypeOptions().filter(option => {
                    // Askerlik durum belgesini sadece erkeklerde göster
                    if (option.value === 'military_service_certificate') {
                      return userData?.gender === 'male';
                    }
                    return true;
                  })
                ]}
              />
            </div>
          </div>

          {/* Mevcut Belgeler */}
          {Object.entries(filteredData.availableCategories).map(([type, docs]) => (
            docs.length > 0 && (
              <div key={type} className="mb-4">
                <div className="d-flex align-items-center mb-3">
                  <h6 className="mb-0 me-2">
                    {DOCUMENT_TYPE_LABELS[type as DocumentType]}
                  </h6>
                  <span className="badge bg-light text-dark">{docs.length} belge</span>
                </div>
                <div className="row g-3">
                  {docs.map((doc) => (
                    <div key={doc.id} className="col-sm-6 col-md-4 col-xl-3">
                      <div className="card h-100 shadow-sm hover-shadow-lg transition-all">
                        <div className="card-body p-3">
                          <div className="d-flex flex-column h-100">

                            
                            {/* Üst Kısım - İkon ve İsim */}
                            <div className="document-icon-wrapper mb-3 text-center">
                              <i className={`${getFileIcon(getFileType(doc.path))} fs-1`}></i>
                              <h6 className="mt-2 mb-0 text-truncate" style={{ maxWidth: '100%' }} title={doc.name}>
                                {doc.name}
                              </h6>
                            </div>
                            
                            {/* Alt Kısım - Detaylar */}
                            <div className="document-details mt-auto">
                              <div className="d-flex justify-content-between align-items-center text-muted small mb-2">
                                <span>
                                  <i className="ti ti-calendar me-1"></i>
                                  {formatDate(doc.createdAt.toString())}
                                </span>
                                <span>
                                  <i className="ti ti-file-size me-1"></i>
                                  {formatFileSize(doc.size)}
                                </span>
                              </div>
                              
                              {/* İndir/Görüntüle Butonu */}
                              <a
                                href={getFileUrl(doc.path)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-light btn-sm w-100 hover-lift"
                              >
                                <i className="ti ti-download me-1"></i>
                                {t('employeesDetail.documents.view')}
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          ))}

          {/* Eksik Belgeler */}
          {filteredData.missingTypes.length > 0 && (
            <div className="mb-4">
              <div className="d-flex align-items-center mb-3">
                <h6 className="text-danger mb-0 me-2">
                  <i className="ti ti-alert-circle me-1"></i>
                  Eksik Belgeler
                </h6>
                <span className="badge bg-danger-subtle text-danger">{filteredData.missingTypes.length} eksik</span>
              </div>
              <div className="row g-3">
                {filteredData.missingTypes.map((type) => (
                  <div key={type} className="col-sm-6 col-md-4 col-xl-3">
                    <div className="card h-100 border-danger border-2 border-dashed">
                      <div className="card-body p-3 text-center">
                        <div className="mb-3">
                          <i className="ti ti-file-off fs-1 text-danger opacity-50"></i>
                        </div>
                        <h6 className="mb-2 text-danger">
                          {DOCUMENT_TYPE_LABELS[type]}
                        </h6>
                        <p className="text-muted small mb-3">Bu belge henüz yüklenmemiş</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Kategorisiz Belgeler */}
          {filteredData.uncategorized.length > 0 && (
            <div className="mb-4">
              <div className="d-flex align-items-center mb-3">
                <h6 className="text-warning mb-0 me-2">
                  <i className="ti ti-question-mark me-1"></i>
                  Kategorisiz Belgeler
                </h6>
                <span className="badge bg-warning-subtle text-warning">{filteredData.uncategorized.length} belge</span>
              </div>
              <div className="row g-3">
                {filteredData.uncategorized.map((doc) => (
                  <div key={doc.id} className="col-sm-6 col-md-4 col-xl-3">
                    <div className="card h-100 shadow-sm hover-shadow-lg transition-all border-warning">
                      <div className="card-body p-3">
                        <div className="d-flex flex-column h-100">

                          
                          {/* Üst Kısım - İkon ve İsim */}
                          <div className="document-icon-wrapper mb-3 text-center">
                            <i className={`${getFileIcon(getFileType(doc.path))} fs-1`}></i>
                            <h6 className="mt-2 mb-0 text-truncate" style={{ maxWidth: '100%' }} title={doc.name}>
                              {doc.name}
                            </h6>
                          </div>
                          
                          {/* Alt Kısım - Detaylar */}
                          <div className="document-details mt-auto">
                            <div className="d-flex justify-content-between align-items-center text-muted small mb-2">
                              <span>
                                <i className="ti ti-calendar me-1"></i>
                                {formatDate(doc.createdAt.toString())}
                              </span>
                              <span>
                                <i className="ti ti-file-size me-1"></i>
                                {formatFileSize(doc.size)}
                              </span>
                            </div>
                            
                            {/* İndir/Görüntüle Butonu */}
                            <a
                              href={getFileUrl(doc.path)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-light btn-sm w-100 hover-lift"
                            >
                              <i className="ti ti-download me-1"></i>
                              {t('employeesDetail.documents.view')}
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Hiç belge yoksa */}
          {documents.length === 0 && (
            <div className="text-center py-5">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Henüz hiç belge yüklenmemiş"
              />
            </div>
          )}
        </div>
      </div>

      <style>
        {`
          .hover-shadow-lg {
            transition: all 0.3s ease;
          }
          .hover-shadow-lg:hover {
            transform: translateY(-3px);
            box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.1) !important;
          }
          .hover-lift {
            transition: all 0.2s ease;
          }
          .hover-lift:hover {
            transform: translateY(-1px);
          }
          .document-icon-wrapper {
            min-height: 80px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          .document-icon-wrapper i {
            font-size: 2.5rem;
          }
          .card {
            border: none;
            background: #fff;
            border-radius: 10px;
          }
          .card:hover {
            border-color: #e0e0e0;
          }
          .text-truncate {
            max-width: 150px;
            display: inline-block;
          }
        `}
      </style>

    </div>
  );
};

export default EmployeesDocuments;
